'use client';
import Link from 'next/link';
import { useState } from 'react';
import { useCart } from '@/contexts/CartContext';
import {
	ShoppingBag,
	Store,
	Minus,
	Plus,
	Truck,
	ShieldCheck,
	Share2,
	MapPin,
	MessageCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

function formatCurrency(n: number) {
	return new Intl.NumberFormat('en-NG', {
		style: 'currency',
		currency: 'NGN',
		maximumFractionDigits: 0,
	}).format(n);
}

export default function ProductDetailsPanel({
	id,
	name,
	price,
	stock,
	store,
	images,
	category,
	rating = 0,
	totalReviews = 0,
	product,
}: {
	id: string;
	name: string;
	price: number;
	stock: number;
	store?:
		| { _id?: string; name?: string; slug?: string }
		| string;
	images?: { url: string }[];
	category?: any;
	rating?: number;
		totalReviews?: number;
	product?: any;
}) {
	const { addItem } = useCart(); // Keep existing hook
	const { user } = useAuth(); // Add useAuth hook
	const [qty, setQty] = useState(1);
	const [busy, setBusy] = useState(false);
	const [added, setAdded] = useState(false);

	// Store extraction logic
	const storeObj =
		typeof store === 'object'
			? store
			: { name: 'Unknown Store' };
	const storeName = storeObj?.name;
	const storeLink =
		typeof store === 'string'
			? `/stores/${store}`
			: `/stores/${storeObj?.slug || storeObj?._id}`;

	async function onAdd(e?: React.MouseEvent) {
		e?.preventDefault();
		if (stock < 1) return;
		setBusy(true);
		try {
			addItem(
				{
					id,
					name,
					price,
					image: images?.[0]?.url || '',
					storeId:
						typeof store === 'object'
							? store._id
							: undefined,
					storeName,
				},
				qty,
			);
			setAdded(true);
			setTimeout(() => setAdded(false), 2000);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm h-fit sticky top-24">
			{/* Category & Rating */}
			<div className="flex justify-between items-start mb-3">
				{category && (
					<span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full uppercase tracking-wider">
						{typeof category === 'object'
							? category.name
							: category}
					</span>
				)}
			</div>

			{/* Title */}
			<h1 className="text-3xl font-extrabold text-[#051449] mb-2 leading-tight">
				{name}
			</h1>

			{/* Store Info */}
			<div className="flex items-center gap-2 mb-6 text-sm text-gray-500 pb-6 border-b border-gray-100">
				<Store className="w-4 h-4" />
				<span>Sold by</span>
				<Link
					href={storeLink}
					className="font-semibold text-blue-600 hover:underline flex items-center gap-1"
				>
					{storeName}
					{(storeObj as any)?.owner?.isAlumni && (
						<ShieldCheck className="w-4 h-4 text-blue-500 fill-blue-100" />
					)}
				</Link>
			</div>

			{/* Price & Stock */}
			<div className="mb-8">
				<div className="flex items-end gap-3 mb-2">
					<span className="text-4xl font-bold text-[#051449]">
						{formatCurrency(price)}
					</span>
				</div>

				{stock > 0 ? (
					<div className="flex items-center gap-1.5 text-sm font-medium text-green-600">
						<Truck className="w-4 h-4" />
						In Stock ({stock} available)
					</div>
				) : (
					<div className="flex items-center gap-1.5 text-sm font-medium text-red-600">
							<Minus className="w-4 h-4" />
						Out of Stock
					</div>
				)}
			</div>

			{/* Actions */}
			<div className="space-y-4">
				{/* Qty Selector */}
				<div className="flex items-center justify-between bg-gray-50 p-1 rounded-xl border border-gray-200">
					<button
						onClick={() => setQty(Math.max(1, qty - 1))}
						disabled={qty <= 1}
						className="w-10 h-10 flex items-center justify-center rounded-lg bg-white shadow-sm text-gray-600 hover:text-black disabled:opacity-50 transition-colors"
					>
						<Minus className="w-4 h-4" />
					</button>
					<span className="text-lg font-semibold text-gray-900 w-12 text-center">
						{qty}
					</span>
					<button
						onClick={() => setQty(Math.min(stock, qty + 1))}
						disabled={qty >= stock}
						className="w-10 h-10 flex items-center justify-center rounded-lg bg-white shadow-sm text-gray-600 hover:text-black disabled:opacity-50 transition-colors"
					>
						<Plus className="w-4 h-4" />
					</button>
				</div>

				{/* Add to Cart Button */}
				{user?.role === 'admin' ? (
					<div className="w-full py-4 bg-gray-100 text-gray-500 rounded-xl text-center font-medium border border-gray-200">
						Admin View Only
					</div>
				) : (
						<button
							onClick={onAdd}
							disabled={stock < 1 || busy}
							className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${added
									? 'bg-green-600 text-white'
									: 'bg-[#FFCF23] text-[#051449] hover:bg-[#ffbf00]'
								} disabled:opacity-70 disabled:cursor-not-allowed`}
						>
							{busy ? (
								<span className="animate-pulse">Adding...</span>
							) : added ? (
								<>
										<ShoppingBag className="w-6 h-6" />
										Added to Cart
									</>
								) : (
									<>
									<ShoppingBag className="w-6 h-6" />
									Add to Cart
								</>
							)}
						</button>
				)}
			</div>
		</div>
	);
}
